import { FormEvent, useEffect } from 'react';
import {
  hideBackButton,
  showBackButton,
  useUpsertQualificationMutation,
} from '../../../../store/features/qualification/qualificationSlice';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import { useForm, UseFormReturn } from 'react-hook-form';
import { GuestUserQualificationCreateInputs } from '../types';
import { yupResolver } from '@hookform/resolvers/yup';
import { guestUserQualificationCreateValidationSchema } from './validation';
import { GuestUserPersonalInput } from './personal-input/GuestUserPersonalInput';
import { QualificationInputs } from '../../types';
import { BaseQueryError, ValidationErrorData } from '../../../../store/types';
import _ from 'lodash';
import { QualificationInputUpsertForm } from '../../shared/QualificationInputUpsertForm';
import { AppIcon } from '../../../app-icon/AppIcon';
import { ChevronRightIcon } from '../../../../assets/icons';

interface GuestUserQualificationCreateFormProps {
  onSubmit: () => void;
  onGuestUserCreate?: () => void;
}

const QUALIFICATION_FORM_FIELDS: (keyof GuestUserQualificationCreateInputs)[] = [
  'GPA',
  'course',
  'educationLevel',
  'faculty',
  'graduatedYear',
  'institute',
  'percentage',
  'rank',
];

export const GuestUserQualificationCreateForm: React.FC<GuestUserQualificationCreateFormProps> = ({
  onSubmit,
  onGuestUserCreate,
}) => {
  const dispatch = useAppDispatch();
  const { guestUser, isPersonalInputVisible } = useAppSelector((state) => ({
    guestUser: state.qualification.guestUser,
    isPersonalInputVisible: state.qualification.isBackButtonVisible,
  }));

  const [upsertQualification, { isLoading, error, isSuccess }] = useUpsertQualificationMutation();

  const form = useForm<GuestUserQualificationCreateInputs>({
    resolver: yupResolver(guestUserQualificationCreateValidationSchema),
    mode: 'onTouched',
  });
  const setError = form.setError;

  const handleGuestUserQualificationInputFormSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    onSubmit();
    if (!guestUser) {
      const isValid = await form.trigger(QUALIFICATION_FORM_FIELDS);
      if (isValid) {
        dispatch(showBackButton());
      }
    }
  };

  const handleGuestUserPersonalInputFormSubmit = (data: GuestUserQualificationCreateInputs): void => {
    upsertQualification(data);
  };

  useEffect(() => {
    if (error) {
      const err = error as BaseQueryError;
      if (err.status === 422) {
        const validationErrorData = err.data as ValidationErrorData;
        let hasErrorInQualificationForm = false;
        let keyOfFirstError: string;
        _.forIn(validationErrorData.errors, (error, key) => {
          if (!keyOfFirstError) {
            keyOfFirstError = key;
          }
          if (QUALIFICATION_FORM_FIELDS.includes(key as keyof GuestUserQualificationCreateInputs)) {
            hasErrorInQualificationForm = true;
            if (!QUALIFICATION_FORM_FIELDS.includes(keyOfFirstError as keyof GuestUserQualificationCreateInputs)) {
              keyOfFirstError = key;
            }
          }
          setError(
            key as keyof GuestUserQualificationCreateInputs,
            { message: error[0] },
            { shouldFocus: key === keyOfFirstError }
          );
        });
        if (hasErrorInQualificationForm) dispatch(hideBackButton());
      }
    }
  }, [error, setError, dispatch]);

  useEffect(() => {
    if (isSuccess) {
      onGuestUserCreate && onGuestUserCreate();
    }
  }, [isSuccess, onGuestUserCreate]);

  return isPersonalInputVisible ? (
    <GuestUserPersonalInput
      isLoading={isLoading}
      form={form}
      onSubmit={form.handleSubmit(handleGuestUserPersonalInputFormSubmit)}
    />
  ) : (
    <QualificationInputUpsertForm
      SubmitButtonLabel="Next"
      isSubmitting={isLoading}
      SubmitButtonProps={{ endIcon: <AppIcon sx={{ fontSize: 'inherit!important' }} Icon={ChevronRightIcon} /> }}
      form={form as unknown as UseFormReturn<QualificationInputs>}
      onSubmit={handleGuestUserQualificationInputFormSubmit}
    />
  );
};
